import { useState, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  TextInput,
  Textarea,
  Badge,
  Alert,
  Button,
  CloseButton,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Users, Info } from 'react-feather';
import { toDateValue } from '../../../../utils/dateHelpers';
import { useInsertAtCursor } from '../../../../hooks';
import { MergeTagChips, PaperformSelect, EmailPreview } from '../sessions/feedback';
import type { PaperformForm, MergeTag } from '../../../../services/api/feedbackSurveys';

export interface SurveyFormState {
  paperform_id: string;
  email_subject: string;
  email_template: string;
  survey_deadline: Date | null;
  auto_send: boolean;
  isDirty: boolean;
  isSaved: boolean;
}

interface SurveyConfigCardProps {
  /** Info banner text at top of card */
  infoBanner: React.ReactNode;
  /** Current form state */
  state: SurveyFormState;
  /** Update form state */
  updateState: (updates: Partial<SurveyFormState>) => void;
  /** Available Paperform forms */
  paperforms: PaperformForm[];
  /** Available merge tags */
  mergeTags: MergeTag[];
  /** Participant count for TO pill */
  participantCount: number;
  /** Cohort name for preview substitution */
  cohortName: string;
  /** Whether to show the Survey Deadline field */
  showDeadline?: boolean;
  /** Primary action button label */
  primaryActionLabel: string;
  /** Primary action handler */
  onPrimaryAction: () => void;
  /** Whether primary action is loading */
  primaryActionLoading?: boolean;
  /** Send test email handler */
  onSendTestEmail: () => void;
  /** Whether test email is sending */
  testEmailSending?: boolean;
  /** Clear configuration handler (shown only when form selected or config saved) */
  onClearConfiguration?: () => void;
  /** Extra content rendered above the standard fields (e.g., modality toggle) */
  headerContent?: React.ReactNode;
  /**
   * MED-942: Optional slot for an extra secondary action rendered IMMEDIATELY
   * to the left of "Send Test Email" in the bottom action row. Used by the
   * Matching Form sub-tab to surface "Pull Historical Responses" — kept as a
   * generic ReactNode slot so the card stays SRP and doesn't accumulate
   * sub-tab-specific knowledge.
   */
  secondaryActionBeforeTestEmail?: React.ReactNode;
  /** Whether the card is read-only */
  readOnly?: boolean;
}

export function SurveyConfigCard({
  infoBanner,
  state,
  updateState,
  paperforms,
  mergeTags,
  participantCount,
  cohortName,
  showDeadline = false,
  primaryActionLabel,
  onPrimaryAction,
  primaryActionLoading = false,
  onSendTestEmail,
  testEmailSending = false,
  onClearConfiguration,
  headerContent,
  secondaryActionBeforeTestEmail,
  readOnly = false,
}: SurveyConfigCardProps) {
  const [lastFocusedField, setLastFocusedField] = useState<'subject' | 'body'>('body');
  const subjectField = useInsertAtCursor<HTMLInputElement>();
  const bodyField = useInsertAtCursor<HTMLTextAreaElement>();

  const handleInsertMergeTag = useCallback((tag: string) => {
    if (lastFocusedField === 'subject') {
      subjectField.insert(
        tag,
        () => state.email_subject,
        (v) => updateState({ email_subject: v }),
      );
    } else {
      bodyField.insert(
        tag,
        () => state.email_template,
        (v) => updateState({ email_template: v }),
      );
    }
  }, [lastFocusedField, subjectField, bodyField, state.email_subject, state.email_template, updateState]);

  const showClearButton = !readOnly && (state.paperform_id || state.isSaved) && onClearConfiguration;
  // Show Select Form X button only when form selected but NOT saved
  const showFormClearX = state.paperform_id && !state.isSaved && !readOnly;

  return (
    <Card withBorder p="lg" radius={8} bg="var(--medley-bg-surface)" style={{ boxShadow: 'none' }}>
      <Stack gap="md">
        {/* Info Banner */}
        <Alert icon={<Info size={16} />} color="gray" variant="light" styles={{ message: { fontSize: 14 } }}>
          {infoBanner}
        </Alert>

        {/* Extra header content (e.g., modality toggle for Post Session) */}
        {headerContent}

        {/* TO Pill */}
        <div>
          <Text size="xs" fw={500} c="dimmed" mb="xs" tt="uppercase">
            To
          </Text>
          <Badge variant="light" color="gray" size="lg" leftSection={<Users size={12} />}>
            All Participants ({participantCount})
          </Badge>
        </div>

        {/* Select Form */}
        <div style={{ position: 'relative' }}>
          <PaperformSelect
            forms={paperforms}
            value={state.paperform_id || null}
            onChange={(value) => updateState({ paperform_id: value || '' })}
          />
          {showFormClearX && (
            <CloseButton
              size="sm"
              style={{
                position: 'absolute',
                right: 36,
                top: 30,
                zIndex: 1,
              }}
              onClick={() => updateState({ paperform_id: '' })}
              aria-label="Clear form selection"
            />
          )}
        </div>

        {/* Subject */}
        <TextInput
          ref={subjectField.ref}
          label={<Text size="xs" fw={500} c="dimmed" tt="uppercase">Subject</Text>}
          placeholder="Enter email subject"
          value={state.email_subject}
          onChange={(e) => updateState({ email_subject: e.target.value })}
          onFocus={() => setLastFocusedField('subject')}
          onSelect={subjectField.trackCursor}
          readOnly={readOnly}
        />

        {/* Survey Deadline */}
        {showDeadline && (
          <DateInput
            label={<Text size="xs" fw={500} c="dimmed" tt="uppercase">Survey Deadline <Text component="span" c="dimmed" fw={400}>(optional)</Text></Text>}
            placeholder="Select deadline date"
            valueFormat="MM/DD/YYYY"
            value={state.survey_deadline}
            onChange={(value) => updateState({ survey_deadline: toDateValue(value) })}
            clearable
            minDate={new Date()}
            readOnly={readOnly}
          />
        )}

        {/* Merge Tags */}
        {!readOnly && (
          <MergeTagChips tags={mergeTags} onInsert={handleInsertMergeTag} />
        )}

        {/* Message */}
        <Textarea
          ref={bodyField.ref}
          label={<Text size="xs" fw={500} c="dimmed" tt="uppercase">Message</Text>}
          placeholder="Enter email message"
          value={state.email_template}
          onChange={(e) => updateState({ email_template: e.target.value })}
          onFocus={() => setLastFocusedField('body')}
          onSelect={bodyField.trackCursor}
          minRows={8}
          autosize
          readOnly={readOnly}
        />

        {/* Email Preview */}
        <EmailPreview
          subject={state.email_subject
            .replace(/\{\{cohort_name\}\}/g, cohortName)
            .replace(/\{\{session_number\}\}/g, '3')}
          body={state.email_template
            .replace(/\{\{first_name\}\}/g, 'Alice')
            .replace(/\{\{last_name\}\}/g, 'Williams')
            .replace(/\{\{email\}\}/g, 'alice.williams@techcorp.com')
            .replace(/\{\{cohort_name\}\}/g, cohortName)
            .replace(/\{\{form_link\}\}/g, '[Survey Link]')
            .replace(/\{\{survey_deadline\}\}/g, state.survey_deadline?.toLocaleDateString() || 'TBD')
            .replace(/\{\{session_number\}\}/g, '3')
            .replace(/\{\{start_date\}\}/g, 'January 14, 2025')}
          recipientName="Alice Williams"
          recipientEmail="alice.williams@techcorp.com"
          surveyLinkText="Complete Survey"
        />

        {/* Action Bar */}
        {!readOnly && (
          <Group
            justify="space-between"
            mt="md"
            pt="md"
            style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
          >
            {showClearButton ? (
              <Button
                variant="default"
                color="gray"
                onClick={onClearConfiguration}
              >
                Clear Configuration
              </Button>
            ) : (
              <div />
            )}

            <Group gap="sm">
              {secondaryActionBeforeTestEmail}
              <Button
                variant="outline"
                color="gray"
                bg="white"
                style={{ borderColor: '#E8E6E3' }}
                onClick={onSendTestEmail}
                loading={testEmailSending}
                disabled={!state.paperform_id}
              >
                Send Test Email
              </Button>
              <Button
                onClick={onPrimaryAction}
                loading={primaryActionLoading}
                disabled={!state.paperform_id || !state.email_subject.trim() || !state.email_template.trim()}
              >
                {primaryActionLabel}
              </Button>
            </Group>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
